/*
 * Copyright (c) 2025-2026 Aegis AO Soft LLC and Alexander Orlov.
 * Confidential and proprietary. Author: Alexander Orlov.
 *
 * Tenant "Terms & Conditions" page: hero + default terms text (i18n), interpolated with the
 * tenant's display name. Mirrors the aegis-ao-rental Terms page's default-terms fallback.
 */

import { useTranslation } from 'react-i18next';
import { useTenant } from '../contexts/TenantContext';
import PageHero from '../components/PageHero';
import { pickLocalized } from '../utils/localized';
import RichHtml, { htmlHasContent } from '../components/RichHtml';

export default function TermsPage() {
  const { t, i18n } = useTranslation();
  const { tenant } = useTenant();
  const name = tenant?.displayName || t('terms.company', 'Our Company');

  // Owner-authored Terms (rich HTML, per language) takes precedence over the bundled default sections.
  const ownerTerms = pickLocalized(tenant?.termsJson, tenant?.terms, i18n.language);

  const sections = [
    { title: t('terms.s1.title', 'Acceptance of Terms'),
      body: t('terms.s1.body', 'By accessing and using {{name}} and booking a vehicle through this site, you agree to be bound by these Terms & Conditions. If you do not agree, please do not use the service.', { name }) },
    { title: t('terms.s2.title', 'Bookings & Eligibility'),
      body: t('terms.s2.body', 'You must hold a valid driver license and meet the minimum age and eligibility requirements to rent a vehicle from {{name}}. A booking is confirmed only once it is approved and any required agreement is signed.', { name }) },
    { title: t('terms.s3.title', 'Payments & Deposits'),
      body: t('terms.s3.body', 'Rental charges, fees, taxes and any security deposit are shown before you confirm. You authorize {{name}} to charge the payment method on file for the rental and for any tolls, fines, damage or other amounts you are responsible for under the rental agreement.', { name }) },
    { title: t('terms.s4.title', 'Cancellations & Changes'),
      body: t('terms.s4.body', 'Cancellation and change rules are shown at booking time and may vary by vehicle and dates. Some bookings, deposits or fees may be non-refundable.') },
    { title: t('terms.s5.title', 'Use of the Vehicle'),
      body: t('terms.s5.body', 'The vehicle must be used lawfully and in line with the rental agreement. You are responsible for tolls, traffic and parking violations, and for returning the vehicle in the condition and at the time agreed.') },
    { title: t('terms.s6.title', 'Liability'),
      body: t('terms.s6.body', 'To the extent permitted by law, {{name}} is not liable for indirect or consequential losses. Nothing in these terms limits liability that cannot be limited by law.', { name }) },
    { title: t('terms.s7.title', 'Contact'),
      body: t('terms.s7.body', 'Questions about these terms? Reach out through the Contact page and we will be happy to help.') },
  ];

  return (
    <>
      <PageHero title={t('terms.title', 'Terms & Conditions')} subtitle={name} />
      <section className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="bg-white rounded-lg shadow-lg p-8 md:p-12 text-gray-700">
          {htmlHasContent(ownerTerms) ? (
            <RichHtml html={ownerTerms} className="leading-relaxed" />
          ) : (
            sections.map((s, i) => (
              <div key={i} className={i === 0 ? '' : 'mt-8'}>
                <h2 className="text-2xl font-bold text-gray-900 mb-3">{s.title}</h2>
                <p className="leading-relaxed">{s.body}</p>
              </div>
            ))
          )}
        </div>
      </section>
    </>
  );
}
